import Select, { components } from "react-select";
import { ReactComponent as DropdownArrowDown } from "../../../../assets/icons/dropdownArrowDown.svg";
import { ReactComponent as DropdownArrowUp } from "../../../../assets/icons/dropdownArrowUp.svg";
import moment from "moment";
import { useEffect, useState } from "react";
import APIVariables from "../../../../services/api-variables";
import { handleCatch } from "../../../../shared/helpers";
import Axios from "../../../../services/Axios";
import useRoles from "../../../../hooks/useRoles";

const DropdownIndicator = (props) => {
  return (
    components.DropdownIndicator && (
      <components.DropdownIndicator {...props}>
        {props.selectProps.menuIsOpen ? (
          <DropdownArrowUp />
        ) : (
          <DropdownArrowDown />
        )}
      </components.DropdownIndicator>
    )
  );
};

/** react-select theming — must stay as style objects */
const selectStyles = {
  singleValue: (provided) => ({
    ...provided,
    color: "#00567a !important",
  }),
  option: (provided, { isFocused }) => ({
    ...provided,
    color: "#00567a !important",
    background: isFocused ? "#e8edee !important" : "white !important",
  }),
};

function NominateDispenser({
  openFinishModal,
  onPrevious,
  setFinishModalContent,
  completePAF,
  pafID,
  dispensingForm,
  setDispensingForm,
}) {
  const [dispenserPoint, setDispenserPoint] = useState([]);
  const [locations, setLocations] = useState([]);
  const { storageData } = useRoles();
  const [fieldsValidation, setFieldsValidation] = useState({
    showErrors: false,
    validation: {
      type: {
        isValid: false,
      },
      location: {
        isValid: false,
      },
      confirmation_risk: {
        isValid: false,
      },
    },
    areErrors: true,
  });

  useEffect(() => {
    getDispensersType();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pafID]);

  useEffect(() => {
    validateFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dispensingForm)]);

  useEffect(() => {
    getLocationOptions();
    setDispensingForm({ ...dispensingForm, location: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensingForm.type]);

  const validateFields = () => {
    const { confirmation_risk, location, type } = dispensingForm;
    let validation = {
      confirmation_risk: {
        isValid: confirmation_risk === true,
      },
      type: {
        isValid: type !== null,
      },
      location: {
        isValid: location !== null,
      },
    };
    let areErrors = Object.values(validation).some((field) => {
      return !field.isValid;
    });
    setFieldsValidation((prevState) => ({
      ...prevState,
      validation: validation,
      areErrors: areErrors,
    }));
  };

  const getLocationOptions = async () => {
    if (!dispensingForm.type) {
      return;
    }
    await Axios.get(
      APIVariables.PRESCRIPTIONS +
      APIVariables.DISPENSER_TYPE_LOCATIONS +
      `${dispensingForm.type?.value}`
    )
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          setLocations(data);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
  };

  const getDispensersType = async () => {
    await Axios.get(
      APIVariables.PRESCRIPTIONS + `${pafID}` + APIVariables.DISPENSER_TYPE
    )
      .then((res) => {
        const { status, data } = res;
        if (status === 200) {
          const { dispenser_types } = data;
          setDispenserPoint(dispenser_types);
        }
      })
      .catch((error) => {
        handleCatch(error);
      });
  };

  const handleCheckBoxChange = (e) => {
    const { name, checked } = e.target;
    setDispensingForm({ ...dispensingForm, [name]: checked });
  };

  const clickComplete = () => {
    if (fieldsValidation.areErrors) {
      setFieldsValidation({ ...fieldsValidation, showErrors: true });
      return;
    }
    let nominate = {
      dispenser_type: dispensingForm.type?.value,
      dispensing_location: dispensingForm.location?.value,
      confirmation_risk: dispensingForm.confirmation_risk,
    };
    setFinishModalContent({
      title: "Prescription Authorisation Form Completed",
      content: "The PAF has been sent to the nominated dispensing location.",
      onFinish: () => {
        completePAF(nominate);
        onPrevious();
      },
    });
    openFinishModal();
  };

  const pharmacistDisplayName = [storageData?.first_name, storageData?.last_name]
    .filter(Boolean)
    .join(" ");

  const confirmationInvalid =
    fieldsValidation.showErrors &&
    !fieldsValidation.validation.confirmation_risk?.isValid;

  return (
    <div
      className="mx-4 relative z-2 mx-auto w-full rounded-[10px] bg-white px-6 py-5 shadow-[0_0_40px_rgba(0,0,0,0.2)] sm:px-10"
      data-testid="paf-nominate-dispenser"
    >
      <div className="flex w-full flex-col gap-4">
        <div
          className="text-lg text-[#00567a]"
          data-testid="paf-nominate-step-label"
        >
          Step 3/3
        </div>
        <div
          className="text-lg font-semibold text-[#00567a]"
          data-testid="paf-nominate-title"
        >
          PAF to Nominated Dispenser*:
        </div>
        <div
          className="text-base text-[#646464]"
          data-testid="paf-nominate-description"
        >
          Please select the nominated dispenser*:
        </div>

        <div
          className="relative mt-1 w-full"
          data-testid="paf-nominate-type-select"
        >
          <Select
            classNamePrefix="custom-select"
            components={{
              DropdownIndicator: DropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            styles={selectStyles}
            options={dispenserPoint.map((dispenser) => ({
              value: dispenser?.id,
              label: dispenser?.value,
            }))}
            name="type"
            value={dispensingForm.type}
            onChange={(opt) =>
              setDispensingForm({ ...dispensingForm, type: opt })
            }
            className="w-full"
            placeholder="Select the dispensing point"
          />
          {fieldsValidation.showErrors &&
            !fieldsValidation.validation.type?.isValid && (
              <span
                className="mt-1 block text-sm font-light italic text-[#e41818]"
                data-testid="paf-nominate-error-type"
              >
                Required
              </span>
            )}
        </div>

        <div
          className="relative mt-5 w-full"
          data-testid="paf-nominate-location-select"
        >
          <Select
            classNamePrefix="custom-select"
            components={{
              DropdownIndicator: DropdownIndicator,
              IndicatorSeparator: () => null,
            }}
            value={dispensingForm.location}
            onChange={(opt) =>
              setDispensingForm({ ...dispensingForm, location: opt })
            }
            styles={selectStyles}
            name="location"
            options={locations.map((location) => ({
              value: location?.id,
              label: location?.name,
              address: location?.address,
            }))}
            className="w-full"
            placeholder="Select a dispensing location"
          />
          {fieldsValidation.showErrors &&
            !fieldsValidation.validation.location?.isValid && (
              <span
                className="mt-1 block text-sm font-light italic text-[#e41818]"
                data-testid="paf-nominate-error-location"
              >
                Required
              </span>
            )}
        </div>

        <div
          className="mt-5 w-full"
          data-testid="paf-nominate-confirmation-section"
        >
          <label
            htmlFor="confirmation_risk"
            className={
              "flex cursor-pointer items-start gap-3 text-base leading-snug text-[#646464] " +
              (confirmationInvalid ? "text-[#e41818]" : "")
            }
          >
            <input
              type="checkbox"
              id="confirmation_risk"
              name="confirmation_risk"
              checked={!!dispensingForm.confirmation_risk}
              className="mt-1 h-5 w-5 shrink-0 rounded border border-[#00567a] accent-[#00567a]"
              onChange={handleCheckBoxChange}
              data-testid="paf-nominate-confirmation-risk"
            />
            <span>
              I confirm that treatment lengths will be limited to a maximum of 4
              weeks supply for women of childbearing potential and a maximum of
              12 weeks for males and women of non-childbearing potential
              patients. For women of child bearing potential dispensing will be
              within 7 days of the date of prescription
            </span>
          </label>
        </div>

        <div
          className="mt-6 flex flex-wrap gap-8 border-t border-[#e8e8e8] pt-6"
          data-testid="paf-nominate-pharmacist-details"
        >
          <div className="flex min-w-50 flex-col gap-1">
            <label className="text-sm font-normal text-[#00567a]">
              Pharmacist name:
            </label>
            <input
              type="text"
              className="border-0 border-b border-[#c8c8c8] bg-[#f8f8f8] text-lg leading-[22px] text-[#646464] outline-none"
              value={pharmacistDisplayName}
              readOnly
              disabled
              data-testid="paf-nominate-pharmacist-name"
            />
          </div>
          <div className="flex min-w-50 flex-col gap-1">
            <label className="text-sm font-normal text-[#00567a]">Date:</label>
            <div
              className="text-lg leading-[22px] text-[#646464]"
              data-testid="paf-nominate-date"
            >
              {moment().format("DD / MM / YYYY")}
            </div>
          </div>
        </div>

        <div
          className="flex flex-col gap-6 pt-10 sm:flex-row sm:items-center sm:justify-between"
          data-testid="paf-nominate-actions"
        >
          <button
            type="button"
            className="cursor-pointer border-none bg-transparent p-0 text-base text-[#8c8c8c] hover:text-[#3c3c3c]"
            onClick={() => onPrevious(true)}
            data-testid="paf-nominate-previous-button"
          >
            Previous
          </button>
          <div className="flex flex-col items-end gap-3 sm:flex-row sm:items-center sm:gap-4">
            <div
              className="text-center text-base leading-[19px] text-[#00567a] sm:text-left"
              data-testid="paf-nominate-mandatory-field-note"
            >
              *symbol indicating a mandatory field
            </div>
            <button
              type="button"
              className="inline-flex h-[46px] min-w-[160px] cursor-pointer items-center justify-center rounded-full border-0 bg-[#00567a] px-6 text-lg font-bold leading-[22px] text-white hover:bg-[#005eb8]"
              onClick={() => clickComplete()}
              data-testid="paf-nominate-complete-button"
            >
              Complete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NominateDispenser;
